import { useContext, createContext, useRef, useState, useEffect } from 'react';

import { RouteScreenContext } from 'components/app/AppContext';

import type { TCardName } from 'components/direct-sales/select-options/config/cards-config';

type ScreenContextType = {
  caveatRef: HTMLElement | null;
  setCaveatRef: (ref: HTMLElement | null) => void;
  caveatItemIdsRef: React.RefObject<string[]>;
  registerCard: (card: TCardName) => void;
  unregisterCard: (card: TCardName) => void;
  registerCta: (cta: string) => void;
  cards: TCardName[];
  ctasRef: React.RefObject<string[]>;
};
/* istanbul ignore next -- @preserve */
export const defaultScreenContext: ScreenContextType = {
  caveatRef: null,
  setCaveatRef: () => {},
  caveatItemIdsRef: { current: [] },
  registerCard: () => undefined,
  unregisterCard: () => undefined,
  registerCta: () => undefined,
  cards: [],
  ctasRef: { current: [] }
};
export const ScreenContext = createContext(defaultScreenContext);

export const useCaveatRegistry = () => {
  const [caveatRef, setCaveatRef] = useState<HTMLElement | null>(null);
  const caveatItemIdsRef = useRef([]);

  useEffect(() => {
    return () => {
      // cleanup when screen is unmounted
      caveatItemIdsRef.current = [];
      setCaveatRef(null);
    };
  }, []);

  return {
    caveatRef,
    setCaveatRef,
    caveatItemIdsRef
  };
};

export const useCardsRegistry = () => {
  const [cards, setCards] = useState<TCardName[]>([]);
  const ctasRef = useRef<string[]>([]);

  const registerCard = (card: TCardName) =>
    setCards((prev) => Array.from(new Set([...prev, card])));

  const unregisterCard = (card: TCardName) =>
    setCards((prev) => prev.filter((c) => c !== card));

  const registerCta = (cta: string) => {
    ctasRef.current = Array.from(new Set([...ctasRef.current, cta]));
  };

  return {
    registerCard,
    unregisterCard,
    registerCta,
    cards,
    ctasRef
  };
};

export const useScreen = () => {
  return useContext(ScreenContext);
};

export const useRouteScreen = () => {
  return useContext(RouteScreenContext);
};
